import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativePageRoot } from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Sparkles, ChevronRight, Camera } from 'lucide-react';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  ${iosNativePageRoot}
`;

const OnboardingCard = styled(Card)`
  max-width: 600px;
  width: 100%;
  animation: slideUp 0.5s ease-out;
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  justify-content: center;
`;

const Step = styled.div<{ $active: boolean }>`
  height: 4px;
  flex: 1;
  background: ${props => props.$active ? theme.colors.primary[500] : theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  transition: all 0.3s ease;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const OptionsGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const OptionButton = styled.button<{ $selected: boolean }>`
  padding: 1.25rem;
  background: ${props => props.$selected ? theme.colors.glow.mint : 'white'};
  border: 2px solid ${props => props.$selected ? theme.colors.primary[500] : theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};

  &:hover {
    border-color: ${theme.colors.primary[400]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const IconEmoji = styled.span`
  font-size: 2rem;
  margin-bottom: 1rem;
  display: block;
  text-align: center;
`;

type Step = 'welcome' | 'skin-type' | 'concerns' | 'camera';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('welcome');
  const [skinType, setSkinType] = useState<string>('');
  const [concerns, setConcerns] = useState<string[]>([]);

  const stepIndex = {
    'welcome': 0,
    'skin-type': 1,
    'concerns': 2,
    'camera': 3,
  }[currentStep];

  const handleNext = () => {
    if (currentStep === 'welcome') {
      setCurrentStep('skin-type');
    } else if (currentStep === 'skin-type') {
      setCurrentStep('concerns');
    } else if (currentStep === 'concerns') {
      setCurrentStep('camera');
    } else if (currentStep === 'camera') {
      navigate('/scan');
    }
  };

  const handleBack = () => {
    if (currentStep === 'skin-type') {
      setCurrentStep('welcome');
    } else if (currentStep === 'concerns') {
      setCurrentStep('skin-type');
    } else if (currentStep === 'camera') {
      setCurrentStep('concerns');
    }
  };

  const toggleConcern = (concern: string) => {
    if (concerns.includes(concern)) {
      setConcerns(concerns.filter(c => c !== concern));
    } else {
      setConcerns([...concerns, concern]);
    }
  };

  const canProceed = () => {
    if (currentStep === 'welcome') return true;
    if (currentStep === 'skin-type') return skinType !== '';
    if (currentStep === 'concerns') return concerns.length > 0;
    if (currentStep === 'camera') return true;
    return false;
  };

  return (
    <PageContainer>
      <OnboardingCard padding="lg">
        <StepIndicator>
          {[0, 1, 2, 3].map(i => (
            <Step key={i} $active={i <= stepIndex} />
          ))}
        </StepIndicator>

        {currentStep === 'welcome' && (
          <>
            <IconEmoji>✨</IconEmoji>
            <Title>Welcome to GlowLens™</Title>
            <Subtitle>
              Let's glow smarter, not harder. We'll help you understand your skin and create a personalized wellness routine just for you.
            </Subtitle>
            <Button fullWidth size="lg" onClick={handleNext}>
              Get Started <ChevronRight size={20} />
            </Button>
          </>
        )}

        {currentStep === 'skin-type' && (
          <>
            <Title>What's your skin type?</Title>
            <Subtitle>
              This helps us personalize your recommendations. Don't worry if you're not sure — you can always change this later.
            </Subtitle>
            <OptionsGrid>
              {['Oily', 'Dry', 'Combination', 'Sensitive', 'Normal', 'Not sure'].map(type => (
                <OptionButton
                  key={type}
                  $selected={skinType === type}
                  onClick={() => setSkinType(type)}
                >
                  {type}
                </OptionButton>
              ))}
            </OptionsGrid>
            <ButtonGroup>
              <Button variant="ghost" onClick={handleBack}>
                Back
              </Button>
              <Button fullWidth disabled={!canProceed()} onClick={handleNext}>
                Continue <ChevronRight size={20} />
              </Button>
            </ButtonGroup>
          </>
        )}

        {currentStep === 'concerns' && (
          <>
            <Title>What are your main concerns?</Title>
            <Subtitle>
              Select all that apply. We'll focus on these areas to give you the best results.
            </Subtitle>
            <OptionsGrid>
              {['Acne breakouts', 'Dark spots', 'Redness', 'Texture', 'Large pores', 'Dryness', 'Oiliness'].map(concern => (
                <OptionButton
                  key={concern}
                  $selected={concerns.includes(concern)}
                  onClick={() => toggleConcern(concern)}
                >
                  {concern}
                </OptionButton>
              ))}
            </OptionsGrid>
            <ButtonGroup>
              <Button variant="ghost" onClick={handleBack}>
                Back
              </Button>
              <Button fullWidth disabled={!canProceed()} onClick={handleNext}>
                Continue <ChevronRight size={20} />
              </Button>
            </ButtonGroup>
          </>
        )}

        {currentStep === 'camera' && (
          <>
            <IconEmoji>📸</IconEmoji>
            <Title>Ready for your first scan?</Title>
            <Subtitle>
              We'll need camera access to analyze your skin. Your photos are private and never shared. Let's capture your glow journey starting point! 💚
            </Subtitle>
            <ButtonGroup>
              <Button variant="ghost" onClick={handleBack}>
                Back
              </Button>
              <Button fullWidth size="lg" onClick={handleNext}>
                <Camera size={20} />
                Open Camera
              </Button>
            </ButtonGroup>
          </>
        )}
      </OnboardingCard>
    </PageContainer>
  );
};

export default OnboardingPage;


